import { useSyncExternalStore } from "react";

/**
 * §Realtime-poll-gate: is a nawy-now SSE stream actually OPEN right now?
 *
 * The board and its sibling queries carried a 60s `refetchInterval` as the guarantee that a dropped
 * event could not leave the tab stale. That guarantee only has to hold while the stream is DOWN — a
 * live stream already pushes every change. Polling underneath a healthy stream buys nothing and costs
 * a visible refetch every minute, which is what put the tab into loading states mid-work.
 *
 * A module-level signal rather than context because producer (the SSE hook, mounted on the lead page)
 * and consumers (query hooks, mounted inside the tab) sit in different branches of the tree, and this
 * is one boolean — not state worth a provider. Counted, not a flag: several subscriptions may share
 * the page, and the poll may only stop while at least one of them is connected.
 *
 * Deliberately scoped to nawy-now: the on-call indicator rides the same driver but its own stream
 * being up says nothing about whether NAWY-NOW events are being delivered (separate feature flag),
 * so only `useNawyNowSse` reports here.
 */
let openStreams = 0;
const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((listener) => listener());

export const markNawyNowStreamOpen = (): void => {
  openStreams += 1;
  emit();
};

export const markNawyNowStreamClosed = (): void => {
  openStreams = Math.max(0, openStreams - 1);
  emit();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Boolean primitive — stable identity, so `useSyncExternalStore` never loops. */
const getSnapshot = (): boolean => openStreams > 0;

export const useNawyNowRealtimeLive = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, () => false);

/** §Realtime poll floor: bounds staleness at 60s whenever push is unavailable. */
const POLL_FLOOR_MS = 60_000;

/**
 * §Realtime-backstop: the poll that still runs WHILE the stream is up.
 *
 * An open EventSource proves the socket connected — it does not prove events are being delivered.
 * Everything between a WM commit and this browser can fail silently with the stream still green: the
 * WM realtime-forwarder's LISTEN client, the WM→CRM publish, the gateway's topic fan-out, or simply a
 * subscription on the wrong journey topic. That class of failure has bitten this integration before, so
 * "live" buys a much slower poll, never no poll at all.
 */
const LIVE_BACKSTOP_MS = 300_000;

/**
 * §Realtime-poll-gate: the freshness policy every nawy-now query shares.
 *
 *   • `refetchInterval` — 60s while the stream is down, 5 min while it is up. Deliberately never
 *     `false`: a live socket is not proof of a live pipeline.
 *   • `refetchOnWindowFocus` — off ONLY while the stream is live, because `useNawyNowSse` already
 *     invalidates once on the down→up reconnect edge, which covers the hidden-tab gap exactly (the SSE
 *     driver closes its stream on `visibilitychange`). While the stream is DOWN that reconnect edge can
 *     never fire, so focus is the only instant catch-up there is and must stay on — without this the
 *     tab could sit stale for a full poll interval on return, which is worse than the churn we removed.
 */
export const realtimeQueryFlags = (live: boolean) =>
  ({
    refetchInterval: live ? LIVE_BACKSTOP_MS : POLL_FLOOR_MS,
    refetchOnWindowFocus: !live,
  }) as const;
